import { useTranslation } from 'react-i18next'

import {
  Box,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
} from '@enechain/ecloud-designsystem'

import { Badge } from '~/components/common/Badge'
import { TABLE_HEADER_CELL_STYLE } from '~/components/common/MasterDataTable'
import { TableMessageRow } from '~/components/common/TableMessageRow'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { convertAreaLabel } from '~/models/area'
import { formatJstDateTimeFull } from '~/models/dateTime'
import type { OperationHistoryRow } from '~/pages/SpotBidding/models/operationHistory'

const COLUMN_COUNT = 13

// design `#spot-oplog-table` のセル: td 14px。 ecloud Table の size recipe は td を 12px に縮める。
const TD = { fontSize: 'md' } as const

const Dash = (): React.ReactElement => (
  <Text as="span" color="Text.lowEmphasis">
    —
  </Text>
)

type Props = {
  rows: readonly OperationHistoryRow[]
  /** 行の操作者の表示名 (解決できなければ undefined)。 */
  resolveOperatorName: (row: OperationHistoryRow) => string | undefined
  /** 行を押した時 (バッチの詳細を開く)。 渡さなければ行は押せない。 */
  onRowClick?: (row: OperationHistoryRow) => void
}

/**
 * オペレーション履歴の表 (AC-BSP-038。 design `#spot-oplog-table`)。 一覧とバッチの詳細ダイアログで
 * 同じ列を使う。 ブロック入札の Leg は design の `oplog-grp` どおりグループにする。
 */
export const OperationHistoryTable = ({
  rows,
  resolveOperatorName,
  onRowClick,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <Box overflowX="auto">
      <TableRoot>
        <TableHeader css={{ '& th': TABLE_HEADER_CELL_STYLE }}>
          <TableRow>
            <TableColumnHeader>
              {t('page:biddingSpot.operationHistory.column.operatedAt')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingSpot.operationHistory.column.operatedBy')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.operationHistory.column.bidKind')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingSpot.operationHistory.column.operation')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingSpot.column.timeBand')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.column.area')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.column.side')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="right">
              {t('page:biddingSpot.operationHistory.column.price')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="right">
              {t('page:biddingSpot.operationHistory.column.volume')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="right">
              {t('page:biddingSpot.column.bidNo')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="right">
              {t('page:biddingSpot.column.deliveryContractCode')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingSpot.operationHistory.column.note')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingSpot.column.workflowId')}
            </TableColumnHeader>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableMessageRow
              colSpan={COLUMN_COUNT}
              message={t('page:biddingSpot.operationHistory.empty')}
            />
          ) : (
            rows.map((row) => {
              const isGrouped = row.groupSize > 1
              const operatedBy = resolveOperatorName(row)
              return (
                <TableRow
                  key={row.key}
                  borderTopWidth={
                    isGrouped && row.groupPos === 0 ? '1px' : undefined
                  }
                  borderTopColor="Border.midEmphasis"
                  borderBottomWidth={
                    isGrouped && row.groupPos === row.groupSize - 1
                      ? '1px'
                      : undefined
                  }
                  borderBottomColor="Border.midEmphasis"
                  cursor={onRowClick === undefined ? undefined : 'pointer'}
                  backgroundColor={isGrouped ? 'Surface.tertiary' : undefined}
                  data-testid="operation-history-row"
                  onClick={
                    onRowClick === undefined ? undefined : () => onRowClick(row)
                  }
                >
                  <TableCell
                    {...TD}
                    whiteSpace="nowrap"
                    css={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {row.operatedAt === undefined ? (
                      <Dash />
                    ) : (
                      formatJstDateTimeFull(row.operatedAt)
                    )}
                  </TableCell>
                  <TableCell {...TD} whiteSpace="nowrap">
                    {operatedBy ?? <Dash />}
                  </TableCell>
                  <TableCell {...TD} textAlign="center" whiteSpace="nowrap">
                    <Badge status="neutral">
                      {t(
                        `page:biddingSpot.operationHistory.kind.${row.bidKind}`,
                      )}
                    </Badge>
                    {row.blockType !== undefined && (
                      <>
                        {' '}
                        <Badge fontSize="xs" status="info">
                          {row.blockType}
                        </Badge>
                      </>
                    )}
                    {row.legRole !== undefined && (
                      <>
                        {' '}
                        <Badge fontSize="xs" status="neutral">
                          {row.legRole}
                        </Badge>
                      </>
                    )}
                  </TableCell>
                  <TableCell {...TD}>
                    <Badge status="neutral">
                      {t(
                        `page:biddingSpot.operationHistory.operation.${row.operation}`,
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell
                    {...TD}
                    whiteSpace="nowrap"
                    css={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {row.timeLabel}
                  </TableCell>
                  <TableCell {...TD} textAlign="center">
                    {convertAreaLabel(row.areaCode)}
                  </TableCell>
                  <TableCell {...TD} textAlign="center">
                    <TradeSideLabel side={row.direction} />
                  </TableCell>
                  <TableCell
                    {...TD}
                    textAlign="right"
                    css={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {row.price.kind === 'market' ? (
                      t('page:biddingSpot.bidTable.marketOrderPrice')
                    ) : row.price.kind === 'limit' ? (
                      row.price.yenPerKwh
                    ) : (
                      <Dash />
                    )}
                  </TableCell>
                  <TableCell
                    {...TD}
                    textAlign="right"
                    css={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {row.volumeLabel ?? <Dash />}
                  </TableCell>
                  <TableCell
                    {...TD}
                    textAlign="right"
                    css={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {row.jepxBidNo ?? <Dash />}
                  </TableCell>
                  <TableCell
                    {...TD}
                    textAlign="right"
                    css={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {row.deliveryContractCode ?? <Dash />}
                  </TableCell>
                  <TableCell {...TD}>{row.reason ?? <Dash />}</TableCell>
                  <TableCell {...TD}>{row.workflowId ?? <Dash />}</TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </TableRoot>
    </Box>
  )
}
